import { useI18n } from '../../i18n/I18nProvider'
import {
  MAX_LENGTH,
  MIN_LENGTH,
  type CostProfileId,
  type Policy,
} from '../../crypto/spec'
import { Chip, Switch, cx } from '../ui/primitives'
import { AlertIcon } from '../ui/icons'

interface AdvancedOptionsProps {
  policy: Policy
  cost: CostProfileId
  onPolicyChange: (next: Policy) => void
  onCostChange: (next: CostProfileId) => void
}

const COST_ORDER: CostProfileId[] = ['fast', 'balanced', 'high']

export function AdvancedOptions({
  policy,
  cost,
  onPolicyChange,
  onCostChange,
}: AdvancedOptionsProps) {
  const { t } = useI18n()
  const patch = (next: Partial<Policy>) => onPolicyChange({ ...policy, ...next })

  const classes = [
    { key: 'lower' as const, label: t.gen.classes.lower },
    { key: 'upper' as const, label: t.gen.classes.upper },
    { key: 'digits' as const, label: t.gen.classes.digits },
    { key: 'symbols' as const, label: t.gen.classes.symbols },
  ]
  const enabledCount = classes.filter((c) => policy[c.key]).length

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-border bg-surface-2/60 p-4">
      <p className="flex items-start gap-2 text-xs text-warn">
        <AlertIcon className="mt-0.5 size-4 shrink-0" />
        {t.gen.advanced.warning}
      </p>

      {/* Comprimento */}
      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <label htmlFor="ard-length" className="text-sm font-medium text-fg">
            {t.gen.length.label}
          </label>
          <span className="font-mono text-sm text-fg-muted tabular-nums">
            {t.gen.length.unit(policy.length)}
          </span>
        </div>
        <input
          id="ard-length"
          type="range"
          min={MIN_LENGTH}
          max={MAX_LENGTH}
          step={1}
          value={policy.length}
          onChange={(event) => patch({ length: Number(event.target.value) })}
          className="h-11 w-full accent-[var(--color-accent)]"
        />
      </div>

      {/* Classes de caracteres */}
      <div>
        <span className="mb-2 block text-sm font-medium text-fg">{t.gen.classes.label}</span>
        <div className="flex flex-wrap gap-2">
          {classes.map(({ key, label }) => {
            const selected = policy[key]
            // Impede desligar o último conjunto: sem alfabeto não há senha.
            const locked = selected && enabledCount === 1
            return (
              <Chip
                key={key}
                selected={selected}
                disabled={locked}
                className={cx(locked && 'cursor-not-allowed opacity-70')}
                onClick={() => patch({ [key]: !selected } as Partial<Policy>)}
              >
                {label}
              </Chip>
            )
          })}
        </div>
        {enabledCount === 1 && (
          <p className="mt-2 text-xs text-fg-subtle">{t.gen.classes.atLeastOne}</p>
        )}
      </div>

      {/* Conjunto de símbolos */}
      {policy.symbols && (
        <div className="animate-rise">
          <span className="mb-2 block text-sm font-medium text-fg">
            {t.gen.symbolSet.label}
          </span>
          <div className="flex flex-wrap gap-2">
            <Chip
              selected={policy.symbolSet === 'safe'}
              onClick={() => patch({ symbolSet: 'safe' })}
            >
              {t.gen.symbolSet.safe}
            </Chip>
            <Chip
              selected={policy.symbolSet === 'extended'}
              onClick={() => patch({ symbolSet: 'extended' })}
            >
              {t.gen.symbolSet.extended}
            </Chip>
          </div>
          <p className="mt-2 font-mono text-xs break-all text-fg-subtle">
            {policy.symbolSet === 'safe'
              ? t.gen.symbolSet.safeHint
              : t.gen.symbolSet.extendedHint}
          </p>
        </div>
      )}

      <Switch
        checked={policy.excludeAmbiguous}
        onChange={(next) => patch({ excludeAmbiguous: next })}
        label={t.gen.ambiguous.label}
        hint={t.gen.ambiguous.hint}
      />

      {/* Custo do Argon2id */}
      <div>
        <span className="mb-2 block text-sm font-medium text-fg">{t.gen.cost.label}</span>
        <div className="flex flex-wrap gap-2">
          {COST_ORDER.map((id) => (
            <Chip key={id} selected={cost === id} onClick={() => onCostChange(id)}>
              {t.gen.cost[id]}
            </Chip>
          ))}
        </div>
        <p className="mt-2 text-xs text-fg-subtle tabular-nums">{t.gen.cost.hints[cost]}</p>
      </div>
    </div>
  )
}
